/**
 * LinksTab - Links between issues (duplicate/related).
 */

import { useState } from 'react'
import { format } from 'date-fns'
import {
  Link2,
  Plus,
  Trash2,
  ExternalLink,
  Copy,
  Loader2,
  AlertTriangle,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useToast } from '@/hooks/use-toast'
import { useLinks, useCreateLink, useDeleteLink } from '@/hooks/useAnnotations'
import { useAnnotationStore } from '@/stores/annotationStore'
import type { LinkType } from '@/types/annotation'

interface LinksTabProps {
  annotationId: number
  studyId: string
}

const LINK_TYPE_LABELS: Record<LinkType, string> = {
  duplicate: 'Duplicate of',
  related: 'Related to',
}

const LINK_TYPE_DESCRIPTIONS: Record<LinkType, string> = {
  duplicate: 'This issue is a duplicate. Marking as duplicate will close this issue.',
  related: 'This issue is related but not a duplicate.',
}

export function LinksTab({ annotationId, studyId }: LinksTabProps) {
  const { toast } = useToast()
  const openDetailPanel = useAnnotationStore((state) => state.openDetailPanel)

  // State
  const [showAddModal, setShowAddModal] = useState(false)
  const [targetIssueId, setTargetIssueId] = useState('')
  const [linkType, setLinkType] = useState<LinkType>('related')
  const [deleteTarget, setDeleteTarget] = useState<number | null>(null)

  // Fetch links
  const { data, isLoading, error } = useLinks(studyId, annotationId)

  // Mutations
  const createLink = useCreateLink(studyId, annotationId)
  const deleteLink = useDeleteLink(studyId, annotationId)

  const handleCreateLink = async () => {
    const targetId = parseInt(targetIssueId, 10)
    if (isNaN(targetId) || targetId <= 0) {
      toast({
        title: 'Validation Error',
        description: 'Please enter a valid issue ID',
        variant: 'destructive',
      })
      return
    }

    if (targetId === annotationId) {
      toast({
        title: 'Validation Error',
        description: 'Cannot link an issue to itself',
        variant: 'destructive',
      })
      return
    }

    try {
      await createLink.mutateAsync({
        targetAnnotationId: targetId,
        linkType,
      })
      setShowAddModal(false)
      setTargetIssueId('')
      setLinkType('related')
    } catch {
      // Error handled by hook
    }
  }

  const handleDeleteLink = async () => {
    if (deleteTarget === null) return

    try {
      await deleteLink.mutateAsync(deleteTarget)
      setDeleteTarget(null)
    } catch {
      // Error handled by hook
    }
  }

  const handleNavigateToIssue = (issueId: number) => {
    openDetailPanel(issueId)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Failed to load links
      </div>
    )
  }

  const links = data?.links || []

  return (
    <div className="space-y-4">
      {/* Add Link Button */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => setShowAddModal(true)}
        className="w-full"
      >
        <Plus className="h-4 w-4 mr-2" />
        Link to Another Issue
      </Button>

      {/* Links List */}
      {links.length === 0 ? (
        <div className="text-center py-8">
          <Link2 className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">No linked issues</p>
          <p className="text-sm text-muted-foreground">
            Link duplicate or related issues to track connections
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {links.map((link) => (
            <div
              key={link.id}
              className="flex items-center gap-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors"
            >
              <div className="flex-shrink-0">
                {link.linkType === 'duplicate' ? (
                  <Copy className="h-5 w-5 text-orange-500" />
                ) : (
                  <Link2 className="h-5 w-5 text-blue-500" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Badge
                    variant={link.linkType === 'duplicate' ? 'destructive' : 'secondary'}
                    className="text-xs"
                  >
                    {LINK_TYPE_LABELS[link.linkType]}
                  </Badge>
                  <span className="font-mono text-sm">#{link.targetAnnotationId}</span>
                </div>
                {link.targetAnnotation?.content && (
                  <p className="text-sm text-muted-foreground truncate mt-1">
                    {link.targetAnnotation.content}
                  </p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Linked by {link.creator?.fullName || 'Unknown'} •{' '}
                  {format(new Date(link.createdAt), 'PP')}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleNavigateToIssue(link.targetAnnotationId)}
                  title="View linked issue"
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setDeleteTarget(link.id)}
                  title="Remove link"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Link Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Link to Another Issue</DialogTitle>
            <DialogDescription>
              Create a link between this issue and another issue.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Link Type</Label>
              <Select value={linkType} onValueChange={(v) => setLinkType(v as LinkType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(LINK_TYPE_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {LINK_TYPE_DESCRIPTIONS[linkType]}
              </p>
            </div>

            <div className="space-y-2">
              <Label>Target Issue ID</Label>
              <Input
                type="number"
                placeholder="Enter issue ID (e.g., 123)"
                value={targetIssueId}
                onChange={(e) => setTargetIssueId(e.target.value)}
              />
            </div>

            {linkType === 'duplicate' && (
              <div className="flex items-start gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200 rounded-lg">
                <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <p className="text-sm">
                  Marking this issue as a duplicate will automatically close it.
                  The target issue will remain open.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAddModal(false)}
              disabled={createLink.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateLink}
              disabled={!targetIssueId || createLink.isPending}
            >
              {createLink.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteTarget !== null} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Link</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this link? The linked issue will not be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteLink}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteLink.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
